// useProjectAccess(projectId) - what may the signed-in user do on ONE
// project. Backed by GET /projects/:id/access, which folds together
// owner-company membership, project ownership and the CRUD permission
// keys, so partner (non-owner) companies come back with every plan /
// schedule / partner / checklist / minutes write = false no matter what
// keys they hold, and partner employees with issueCreate = false.
//
//   const { access, loading, can, isOwnerCompany } = useProjectAccess(projectId);
//   if (can.ganttEdit) ...       // safe before load: everything false
//
// `can` is always an object (all false until loaded / when the project
// is unknown), so pages can gate synchronously.
import { useEffect, useState, useMemo } from "react";
import { url, tokenRef } from "api";

const EMPTY = Object.freeze({
  isOwnerCompany: false,
  isProjectOwner: false,
  participantRole: "",
  can: Object.freeze({}),
});

const cache = new Map(); // projectId -> { at, data }
const TTL = 60 * 1000;

export async function fetchProjectAccess(projectId, { force = false } = {}) {
  if (!projectId) return EMPTY;
  const hit = cache.get(projectId);
  if (!force && hit && Date.now() - hit.at < TTL) return hit.data;
  const token = localStorage.getItem(tokenRef);
  const resp = await fetch(`${url}/projects/${projectId}/access`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw Object.assign(new Error("access lookup failed"), { status: resp.status });
  const data = await resp.json();
  cache.set(projectId, { at: Date.now(), data });
  return data;
}

export function invalidateProjectAccess(projectId) {
  if (projectId) cache.delete(projectId);
  else cache.clear();
}

export default function useProjectAccess(projectId) {
  const [access, setAccess] = useState(() => cache.get(projectId)?.data || null);
  const [loading, setLoading] = useState(!access);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!projectId) { setAccess(null); setLoading(false); return undefined; }
    setLoading(true);
    fetchProjectAccess(projectId)
      .then((d) => { if (alive) { setAccess(d); setError(null); } })
      .catch((e) => { if (alive) { setAccess(null); setError(e); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  const can = useMemo(() => access?.can || EMPTY.can, [access]);
  return {
    access,
    loading,
    error,
    can,
    isOwnerCompany: !!access?.isOwnerCompany,
    isProjectOwner: !!access?.isProjectOwner,
    // Convenience: anything that edits the plan / schedule
    canEditPlan: !!(can.ganttAdd || can.ganttEdit || can.ganttDelete),
    // A partner whose admin granted "Edit their plan rows": they may move
    // dates and status on the activities THEIR company is responsible
    // for, and nothing else. Kept separate from canEditPlan because that
    // one opens owner-side machinery - adding rows, importing P6, taking
    // baselines - which is not what was granted.
    canEditOwnPlanRows: !!can.ganttEditOwnRows,
    canManagePartners: !!(can.participantAdd || can.participantEdit || can.participantDelete),
  };
}
